import { create } from 'zustand';

async function getSupabase() {
  const { supabase } = await import('@/lib/customSupabaseClient');
  return supabase;
}

export const useEmailStore = create((set, get) => ({
  // Dashboard stats
  stats: null,
  statsLoading: false,

  // Campaigns
  campaigns: [],
  campaignsLoading: false,

  // Email logs
  logs: [],
  logsTotal: 0,
  logsLoading: false,

  // Outbox queue / deliveries (first-party platform)
  queue: [],
  queueLoading: false,
  deliveries: [],
  deliveriesLoading: false,

  // Client alerts
  alerts: [],
  alertsLoading: false,
  scanResult: null,

  // Programs (for audience builder)
  programs: [],
  programsLoading: false,

  // AI generation
  generating: false,
  generatedEmails: [],

  // Sending
  sending: false,
  sendResult: null,

  // Transactional template registry / preview (coach email-template)
  templateRegistry: [],
  templateRegistryLoading: false,
  templatePreview: null,
  templatePreviewLoading: false,
  templateContent: null,
  templateContentLoading: false,
  templateSaving: false,
  templatePublishing: false,
  templateTestSending: false,

  // --- Dashboard Stats (RPC) ---
  fetchStats: async () => {
    set({ statsLoading: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc('admin_get_email_dashboard_stats');
      if (error) throw error;
      set({ stats: data, statsLoading: false });
    } catch (err) {
      console.error('Failed to fetch email stats:', err);
      set({ stats: null, statsLoading: false });
    }
  },

  // --- Campaigns (direct Supabase queries) ---
  fetchCampaigns: async (status) => {
    set({ campaignsLoading: true });
    try {
      const supabase = await getSupabase();
      let query = supabase
        .from('email_campaigns')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (status) query = query.eq('status', status);

      const { data, error } = await query;
      if (error) throw error;
      set({ campaigns: data || [], campaignsLoading: false });
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
      set({ campaigns: [], campaignsLoading: false });
    }
  },

  createCampaign: async (campaign) => {
    const supabase = await getSupabase();
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('email_campaigns')
      .insert({
        name: campaign.name,
        type: campaign.type || 'campaign',
        subject: campaign.subject || campaign.name,
        body_html: campaign.body_html || null,
        target_filter: campaign.target_filter || {},
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    const campaigns = get().campaigns;
    set({ campaigns: [data, ...campaigns] });
    return data;
  },

  updateCampaign: async (campaignId, updates) => {
    const supabase = await getSupabase();
    const { data, error } = await supabase
      .from('email_campaigns')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', campaignId)
      .select()
      .single();

    if (error) throw error;
    set({
      campaigns: get().campaigns.map((c) => (c.id === campaignId ? data : c)),
    });
    return data;
  },

  sendCampaign: async (campaignId, isTest = false) => {
    set({ sending: true, sendResult: null });
    try {
      const supabase = await getSupabase();

      const { data: campaign } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (!campaign) throw new Error('Campaign not found');
      if (!campaign.subject || !campaign.body_html) {
        throw new Error('Campaign is missing subject or body. Edit the campaign before sending.');
      }

      const { data: audience } = await supabase.rpc(
        'admin_get_campaign_audience',
        { p_filter: campaign.target_filter || {} }
      );

      if (!audience || audience.length === 0) throw new Error('No audience found');

      if (!isTest) {
        await supabase
          .from('email_campaigns')
          .update({ status: 'sending', total_recipients: audience.length, updated_at: new Date().toISOString() })
          .eq('id', campaignId);
      }

      const { data, error } = await supabase.functions.invoke('email-send', {
        body: {
          program_id: campaign.target_filter?.program_id,
          subject: campaign.subject,
          html: campaign.body_html,
          campaign_id: campaignId,
          is_test: isTest,
        },
      });

      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      set({ sending: false, sendResult: data });
      return data;
    } catch (err) {
      set({ sending: false, sendResult: { error: err.message } });
      throw err;
    }
  },

  // --- Outbox queue / deliveries (admin RPCs) ---
  fetchQueue: async (params = {}) => {
    set({ queueLoading: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc('admin_get_email_queue', {
        p_limit: params.limit || 50,
        p_status: params.status || null,
        p_template_key: params.template_key || null,
      });
      if (error) throw error;
      set({ queue: data || [], queueLoading: false });
    } catch (err) {
      console.error('Failed to fetch email queue:', err);
      set({ queue: [], queueLoading: false });
    }
  },

  fetchDeliveries: async (params = {}) => {
    set({ deliveriesLoading: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc('admin_get_email_deliveries', {
        p_limit: params.limit || 50,
        p_status: params.status || null,
        p_template_key: params.template_key || null,
      });
      if (error) throw error;
      set({ deliveries: data || [], deliveriesLoading: false });
    } catch (err) {
      console.error('Failed to fetch email deliveries:', err);
      set({ deliveries: [], deliveriesLoading: false });
    }
  },

  // --- Email Logs (direct Supabase queries) ---
  fetchLogs: async (params = {}) => {
    set({ logsLoading: true });
    try {
      const supabase = await getSupabase();
      let query = supabase
        .from('email_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .limit(params.limit || 50);

      if (params.campaign_id) query = query.eq('campaign_id', params.campaign_id);
      if (params.user_id) query = query.eq('user_id', params.user_id);
      if (params.status) query = query.eq('status', params.status);
      if (!params.show_tests) query = query.not('metadata->>is_test', 'eq', 'true');
      if (params.offset) query = query.range(params.offset, params.offset + (params.limit || 50) - 1);

      const { data, error, count } = await query;
      if (error) throw error;
      set({ logs: data || [], logsTotal: count || 0, logsLoading: false });
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      set({ logs: [], logsTotal: 0, logsLoading: false });
    }
  },

  // --- Client Alerts (direct Supabase queries) ---
  fetchAlerts: async (params = {}) => {
    set({ alertsLoading: true });
    try {
      const supabase = await getSupabase();
      let query = supabase
        .from('client_alerts')
        .select('*, profiles:user_id(full_name, email)')
        .order('created_at', { ascending: false });

      if (params.alert_type) query = query.eq('alert_type', params.alert_type);
      if (params.severity) query = query.eq('severity', params.severity);
      if (!params.show_resolved) query = query.eq('is_resolved', false);

      const { data, error } = await query;
      if (error) throw error;
      set({ alerts: data || [], alertsLoading: false });
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      set({ alerts: [], alertsLoading: false });
    }
  },

  scanIssues: async () => {
    set({ scanResult: null });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc('admin_detect_client_issues');
      if (error) throw error;
      set({ scanResult: data });
      await get().fetchAlerts();
      return data;
    } catch (err) {
      console.error('Failed to scan issues:', err);
      throw err;
    }
  },

  resolveAlert: async (alertId) => {
    const supabase = await getSupabase();
    await supabase
      .from('client_alerts')
      .update({ is_resolved: true, resolved_at: new Date().toISOString() })
      .eq('id', alertId);
    set({
      alerts: get().alerts.map((a) =>
        a.id === alertId ? { ...a, is_resolved: true } : a
      ),
    });
  },

  // --- Programs (RPC) ---
  fetchPrograms: async () => {
    set({ programsLoading: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc('admin_get_programs_with_user_counts');
      if (error) throw error;
      set({ programs: data || [], programsLoading: false });
    } catch (err) {
      console.error('Failed to fetch programs:', err);
      set({ programs: [], programsLoading: false });
    }
  },

  // --- AI Generation (Edge Function) ---
  generateWithAI: async ({ userIds, emailType, customInstructions }) => {
    set({ generating: true, generatedEmails: [] });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('email-generate', {
        body: {
          user_ids: userIds,
          email_type: emailType,
          custom_instructions: customInstructions,
          mode: 'generate',
        },
      });
      if (error) {
        const msg = error?.message || error?.context?.body || JSON.stringify(error);
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      if (!data?.results || data.results.length === 0) throw new Error('No results returned from AI');
      const firstResult = data.results[0];
      if (firstResult.error) throw new Error(firstResult.error);
      set({ generating: false, generatedEmails: data.results });
      return data.results;
    } catch (err) {
      console.error('generateWithAI error:', err);
      set({ generating: false, generatedEmails: [] });
      throw err;
    }
  },

  polishWithAI: async ({ userIds, emailType, draftSubject, draftBody, customInstructions }) => {
    set({ generating: true, generatedEmails: [] });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('email-generate', {
        body: {
          user_ids: userIds,
          email_type: emailType,
          custom_instructions: customInstructions,
          mode: 'polish',
          draft_subject: draftSubject,
          draft_body: draftBody,
        },
      });
      if (error) {
        const msg = error?.message || error?.context?.body || JSON.stringify(error);
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      if (!data?.results || data.results.length === 0) throw new Error('No results returned from AI');
      const firstResult = data.results[0];
      if (firstResult.error) throw new Error(firstResult.error);
      set({ generating: false, generatedEmails: data.results });
      return data.results;
    } catch (err) {
      console.error('polishWithAI error:', err);
      set({ generating: false, generatedEmails: [] });
      throw err;
    }
  },

  // --- Send Email (Edge Function) ---
  sendEmail: async ({
    userId,
    toEmail,
    toName,
    subject,
    html,
    programId,
    allConsented,
    campaignId,
    isTest,
    hero,
    videos,
    transactional,
    transactionalReason,
    external,
    language,
    messageClass,
  }) => {
    set({ sending: true, sendResult: null });
    try {
      const supabase = await getSupabase();
      const payload = { subject, html, is_test: isTest };
      if (userId) payload.user_id = userId;
      if (toEmail) payload.to_email = toEmail;
      if (toName) payload.to_name = toName;
      if (programId) payload.program_id = programId;
      if (allConsented) payload.all_consented = true;
      if (campaignId) payload.campaign_id = campaignId;
      if (hero && hero.headline) payload.hero = hero;
      if (videos && videos.length > 0) payload.videos = videos;
      if (transactional) {
        payload.transactional = true;
        if (transactionalReason) payload.transactional_reason = transactionalReason;
      }
      if (external) payload.external = true;
      if (language) payload.language = language;
      if (messageClass) payload.message_class = messageClass;

      const { data, error } = await supabase.functions.invoke('email-send', {
        body: payload,
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      if (data?.success === false) {
        throw new Error(data.error || 'Email send failed');
      }
      set({ sending: false, sendResult: data });
      return data;
    } catch (err) {
      set({ sending: false, sendResult: { error: err.message } });
      throw err;
    }
  },

  // --- Duplicate send check ---
  checkRecentBulkSend: async (subject) => {
    try {
      const supabase = await getSupabase();
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('email_logs')
        .select('id, created_at, to_email')
        .eq('subject', subject)
        .neq('status', 'failed')
        .gte('created_at', twentyFourHoursAgo)
        .limit(5);
      if (error) return null;
      if (!data || data.length === 0) return null;
      return { sentAt: data[0].created_at, count: data.length };
    } catch {
      return null;
    }
  },

  // --- Reset ---
  clearSendResult: () => set({ sendResult: null }),
  clearGeneratedEmails: () => set({ generatedEmails: [] }),

  fetchTemplateRegistry: async () => {
    set({ templateRegistryLoading: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('email-template', {
        body: { action: 'list' },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      set({
        templateRegistry: data?.templates || [],
        templateRegistryLoading: false,
      });
      return data?.templates || [];
    } catch (err) {
      console.error('fetchTemplateRegistry error:', err);
      set({ templateRegistry: [], templateRegistryLoading: false });
      throw err;
    }
  },

  renderTemplatePreview: async ({
    templateKey,
    locale,
    payload,
    recipientName,
    useDraft = true,
    subject,
    bodyHtml,
    hero,
    videos,
    paragraphs,
    silent = false,
  }) => {
    set({ templatePreviewLoading: true });
    try {
      const supabase = await getSupabase();
      const body = {
        action: 'render',
        templateKey,
        locale,
        payload: payload || {},
        recipientName: recipientName || '',
        useDraft,
      };
      if (useDraft && typeof subject === 'string' && subject.trim() && typeof bodyHtml === 'string') {
        body.subject = subject.trim();
        body.bodyHtml = bodyHtml;
        body.hero = hero ?? null;
        body.videos = videos ?? [];
      }
      if (useDraft && Array.isArray(paragraphs)) {
        body.paragraphs = paragraphs;
      }
      const { data, error } = await supabase.functions.invoke('email-template', { body });
      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      set({ templatePreview: data, templatePreviewLoading: false });
      return data;
    } catch (err) {
      console.error('renderTemplatePreview error:', err);
      set({ templatePreview: null, templatePreviewLoading: false });
      if (!silent) throw err;
    }
  },

  fetchTemplateContent: async ({ templateKey = 'activation_confirmed', locale = 'de' }) => {
    set({ templateContentLoading: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('email-template', {
        body: { action: 'getContent', templateKey, locale },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      set({ templateContent: data.content, templateContentLoading: false });
      return data.content;
    } catch (err) {
      console.error('fetchTemplateContent error:', err);
      set({ templateContent: null, templateContentLoading: false });
      throw err;
    }
  },

  saveTemplateDraft: async ({
    templateKey = 'activation_confirmed',
    locale,
    subject,
    bodyHtml,
    hero,
    videos,
  }) => {
    set({ templateSaving: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('email-template', {
        body: {
          action: 'saveDraft',
          templateKey,
          locale,
          subject,
          bodyHtml,
          hero: hero ?? null,
          videos: videos ?? [],
        },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      set({ templateContent: data.content, templateSaving: false });
      return data.content;
    } catch (err) {
      console.error('saveTemplateDraft error:', err);
      set({ templateSaving: false });
      throw err;
    }
  },

  publishTemplate: async ({ templateKey = 'activation_confirmed', locale }) => {
    set({ templatePublishing: true });
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke('email-template', {
        body: { action: 'publish', templateKey, locale },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      set({ templateContent: data.content, templatePublishing: false });
      return data.content;
    } catch (err) {
      console.error('publishTemplate error:', err);
      set({ templatePublishing: false });
      throw err;
    }
  },

  sendTemplateTest: async ({
    templateKey,
    locale,
    payload,
    recipientName,
    useDraft = true,
    subject,
    bodyHtml,
    hero,
    videos,
    paragraphs,
  }) => {
    set({ templateTestSending: true });
    try {
      const supabase = await getSupabase();
      const body = {
        action: 'sendTest',
        templateKey,
        locale,
        payload: payload || {},
        recipientName: recipientName || '',
        useDraft,
      };
      if (useDraft && typeof subject === 'string' && subject.trim() && typeof bodyHtml === 'string') {
        body.subject = subject.trim();
        body.bodyHtml = bodyHtml;
        body.hero = hero ?? null;
        body.videos = videos ?? [];
      }
      if (useDraft && Array.isArray(paragraphs)) {
        body.paragraphs = paragraphs;
      }
      const { data, error } = await supabase.functions.invoke('email-template', { body });
      if (error) {
        let msg = error.message;
        try {
          const ctx = error.context;
          if (ctx && typeof ctx.json === 'function') {
            const errBody = await ctx.json();
            if (errBody?.error) msg = errBody.error;
          }
        } catch (_) { /* use original message */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      set({ templateTestSending: false });
      return data;
    } catch (err) {
      console.error('sendTemplateTest error:', err);
      set({ templateTestSending: false });
      throw err;
    }
  },

  clearTemplatePreview: () => set({ templatePreview: null }),
}));
